/**
 * MergeFailuresPanel - renders per-file merge failures (#411).
 *
 * A merge failure means the adapter wrote an output parquet but the DuckLake
 * MERGE into the destination table failed, so the data was silently dropped
 * even though the run may report success/partial. Surfacing these closes the
 * #402/#405 per-file inspectability gap.
 */
import { GitMerge, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { MergeFailure } from '@/types/connectors';

export interface MergeFailuresPanelProps {
  failures?: MergeFailure[] | null;
  className?: string;
}

export function MergeFailuresPanel({ failures, className }: MergeFailuresPanelProps) {
  if (!Array.isArray(failures) || failures.length === 0) {
    return null;
  }

  return (
    <div
      className={cn('rounded-md border bg-card', className)}
      data-testid="merge-failures-panel"
    >
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <GitMerge className="h-4 w-4 text-destructive" aria-hidden />
        <div className="text-sm font-medium">Merge failures</div>
        <Badge variant="outline" className="ml-auto text-xs">
          {failures.length} file{failures.length === 1 ? '' : 's'}
        </Badge>
      </div>
      <ul className="divide-y">
        {failures.map((f, idx) => (
          <li
            key={`${f.resource}-${idx}`}
            className="space-y-1 px-3 py-2"
            data-testid="merge-failure-item"
          >
            <div className="flex items-center gap-2">
              <XCircle className="h-4 w-4 text-destructive" aria-hidden />
              <span className="text-sm font-medium truncate" title={f.resource}>
                {f.resource}
              </span>
              <Badge variant="destructive" className="text-xs">
                {f.error_type}
              </Badge>
            </div>
            {(f.account_id || f.stage) && (
              <div className="text-xs text-muted-foreground" data-testid="merge-failure-context">
                {[
                  f.account_id && `Account ${f.account_id}`,
                  f.stage && `Stage ${f.stage}`,
                  f.level && `Level ${f.level}`,
                  f.breakdown && `Breakdown ${f.breakdown}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </div>
            )}
            {f.file && (
              <div className="text-xs text-muted-foreground break-all">{f.file}</div>
            )}
            <p className="text-sm whitespace-pre-wrap break-words">{f.error_message}</p>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default MergeFailuresPanel;
